import { useEffect, useRef, useState } from 'react'

/**
 * Smoothly animates from the previously-rendered value to the current `value`
 * over `durationMs`, easing with a cubic ease-out so the number "settles" at
 * the end. The numeric output is run through `format` so the consuming
 * component decides how to display it (currency, percent, plain).
 *
 * **Why this exists.** A debt-recovery tool feels like a calculator until the
 * numbers move. The act of seeing the total tick down — even by a few pixels
 * over half a second — is the difference between "this is a spreadsheet" and
 * "this is reflecting my progress." The animation is intentionally subtle:
 * 500-600ms, ease-out (no overshoot, no spring-back), no color flash. Calm
 * authority, not Robinhood confetti.
 *
 * **Reduced motion.** Honors `prefers-reduced-motion: reduce` by snapping
 * straight to the new value without any animation. WCAG 2.3.3 requires this
 * for motion that isn't essential to the content.
 *
 * **No new deps.** Uses requestAnimationFrame directly. ~40 lines of code.
 * Don't add react-spring or framer-motion just for number tweens.
 */
export interface AnimatedNumberProps {
  /** The target value. Animation re-runs each time this changes. */
  value: number
  /** Render fn — typically a currency or percent formatter. */
  format: (n: number) => string
  /** Animation duration in milliseconds. Default 500. */
  durationMs?: number
  /** Optional className passed through to the wrapping span. */
  className?: string
}

/**
 * Cubic ease-out: starts fast, settles slowly. Feels like a number coming to
 * rest rather than slamming into place. Maps t ∈ [0, 1] → eased ∈ [0, 1].
 */
function easeOutCubic(t: number): number {
  const inv = 1 - t
  return 1 - inv * inv * inv
}

export function AnimatedNumber({
  value,
  format,
  durationMs = 500,
  className,
}: AnimatedNumberProps) {
  // `displayed` is the currently rendered numeric value. Starts at `value` so
  // first paint doesn't animate from 0 (which would feel jarring on initial
  // load — we want animation only on subsequent changes).
  const [displayed, setDisplayed] = useState(value)
  // Track the previous *target* so we can interpolate from where we last
  // rendered, not from `displayed` (which may itself be mid-animation).
  const fromRef = useRef(value)
  // Cache the rAF id so we cancel any in-flight tween when value changes mid-animation.
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    // Bail early if the value didn't actually change (effect could fire on
    // unrelated re-renders).
    if (value === fromRef.current) {
      setDisplayed(value)
      return
    }

    // Honor user accessibility preference — snap, don't animate.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion) {
      fromRef.current = value
      setDisplayed(value)
      return
    }

    const start = fromRef.current
    const end = value
    const startTime = performance.now()

    const tick = (now: number) => {
      const elapsed = now - startTime
      const t = Math.min(1, elapsed / durationMs)
      const eased = easeOutCubic(t)
      const current = start + (end - start) * eased
      setDisplayed(current)
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = end
        rafRef.current = null
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      // Lock in whatever was last rendered as the next animation's starting
      // point — prevents jumps when the value changes mid-tween.
      fromRef.current = displayed
    }
    // `displayed` is intentionally NOT in the deps array — including it would
    // restart the animation on every frame. Same for the ref-only fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, durationMs])

  return <span className={className}>{format(displayed)}</span>
}
